import { useMemo, useState } from 'react';
import { Button, Chip, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import {
  db,
  type CopyConfig,
  type DbServerRef,
  type Job,
  type JobKind,
  type Schedule,
  type ScheduleInput,
} from '../../api/db';
import { EmptyState, ErrorBox, Loading, TableScroller, useAsync, useNotice } from '../../components/common';
import { EditorDialog, IdField, SLUG, SwitchField, useEditorForm } from '../../components/registry';
import { formatDateTime, formatRelative } from '../../lib/format';
import { CRON_PRESETS, cronToText, looksLikeCron } from './cron';
import {
  canRunKind,
  ConfirmDialog,
  DatabasePicker,
  dbErrorText,
  serverName,
  Target,
  useServerDatabases,
  type DbScopes,
} from './shared';

export const DEFAULT_TZ = 'Europe/Stockholm';

/** IANA zones of this browser, the default first. */
export function timeZones(): string[] {
  let zones: string[];
  try {
    zones =
      (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ??
      [];
  } catch {
    zones = [];
  }
  const all = new Set([DEFAULT_TZ, 'UTC', ...zones]);
  return [...all];
}

interface Form {
  id: string;
  name: string;
  kind: JobKind;
  cron: string;
  timezone: string;
  serverId: string;
  database: string;
  configId: string;
  enabled: boolean;
}

const toForm = (s: Schedule): Form => ({
  id: s.id,
  name: s.name,
  kind: s.kind,
  cron: s.cron,
  timezone: s.timezone,
  serverId: s.backup?.serverId ?? '',
  database: s.backup?.database ?? '',
  configId: s.configId ?? '',
  enabled: s.enabled,
});

export function toScheduleInput(f: Form): ScheduleInput {
  return {
    name: f.name.trim(),
    kind: f.kind,
    cron: f.cron.trim().replace(/\s+/g, ' '),
    timezone: f.timezone,
    backup: f.kind === 'backup' ? { serverId: f.serverId, database: f.database } : null,
    configId: f.kind === 'copy' ? f.configId : null,
    enabled: f.enabled,
  };
}

const inputOf = (s: Schedule): ScheduleInput => toScheduleInput(toForm(s));

/** Kind options: a kind the caller may not run is shown but disabled. */
export const kindOptions = (scopes: DbScopes) => [
  { value: 'backup', label: scopes.backup ? 'Backup' : 'Backup (needs db:backup)', disabled: !scopes.backup },
  { value: 'copy', label: scopes.copy ? 'Copy' : 'Copy (needs db:copy)', disabled: !scopes.copy },
];

function ScheduleForm({
  initial,
  isNew,
  scopes,
  servers,
  configs,
  onSaved,
  onClose,
}: {
  initial: Form;
  isNew: boolean;
  scopes: DbScopes;
  servers: DbServerRef[];
  configs: CopyConfig[];
  onSaved: (s: Schedule) => void;
  onClose: () => void;
}) {
  const f = useEditorForm<Form>(initial, isNew);
  const { v, set } = f;
  const dbs = useServerDatabases(v.kind === 'backup' ? v.serverId : '');
  const zones = useMemo(timeZones, []);
  const text = cronToText(v.cron);
  const cronOk = looksLikeCron(v.cron);
  const targetOk = v.kind === 'backup' ? !!v.serverId && !!v.database : !!v.configId;
  const canSave =
    !!v.name.trim() && SLUG.test(v.id) && cronOk && targetOk && !!v.timezone && canRunKind(scopes, v.kind);
  const save = () =>
    void f.run(async () => {
      onSaved(await db.schedules.put(v.id, toScheduleInput(v)));
    });
  return (
    <EditorDialog
      title={isNew ? 'New schedule' : `Schedule ${v.id}`}
      error={f.error}
      busy={f.busy}
      canSave={canSave}
      onSave={save}
      onClose={onClose}
    >
      <TextField label="Name" value={v.name} onChange={(e) => set('name', e.target.value)} required />
      <IdField value={v.id} isNew={isNew} onChange={f.setId} example="acme-nightly-backup" />
      <Select
        label="Kind"
        value={v.kind}
        onChange={(k) => set('kind', k as JobKind)}
        options={kindOptions(scopes)}
        disabled={!isNew}
      />
      {v.kind === 'backup' ? (
        <>
          <Select
            label="Server"
            value={v.serverId}
            placeholder="Pick a server"
            onChange={(id) => f.setV((c) => ({ ...c, serverId: id, database: '' }))}
            options={servers.map((s) => ({ value: s.id, label: s.name || s.id }))}
          />
          <label className="nyk-ui-field">
            <span>Database</span>
            <DatabasePicker databases={dbs.names} value={v.database} onChange={(d) => set('database', d)} />
          </label>
        </>
      ) : (
        <Select
          label="Copy config"
          value={v.configId}
          placeholder={configs.length ? 'Pick a copy config' : 'No copy configs yet'}
          onChange={(id) => set('configId', id)}
          options={configs.map((c) => ({
            value: c.id,
            label: `${c.name || c.id} (${c.sourceDatabase} → ${serverName(servers, c.targetServerId)}/${c.targetDatabase})`,
          }))}
        />
      )}
      <div className="nyk-ui-field nyk-span2">
        <span>Presets</span>
        <div className="nyk-inline dbx-presets">
          {CRON_PRESETS.map((p) => (
            <Chip
              key={p.cron}
              size="sm"
              selected={v.cron.trim() === p.cron}
              onClick={() => set('cron', p.cron)}
            >
              {p.label}
            </Chip>
          ))}
        </div>
      </div>
      <TextField
        label="Cron"
        value={v.cron}
        onChange={(e) => set('cron', e.target.value)}
        spellCheck={false}
        className="dbx-mono"
        error={v.cron && !cronOk ? 'five fields: minute hour day-of-month month day-of-week' : undefined}
        hint={text ?? 'minute hour day-of-month month day-of-week'}
      />
      <Select
        label="Time zone"
        value={v.timezone}
        onChange={(tz) => set('timezone', tz)}
        options={zones.map((z) => ({ value: z, label: z }))}
      />
      <SwitchField label="Enabled" checked={v.enabled} onChange={(e) => set('enabled', e)} />
    </EditorDialog>
  );
}

export function SchedulesTab({
  servers,
  scopes,
  onJob,
  onOpenJob,
}: {
  servers: DbServerRef[];
  scopes: DbScopes;
  onJob: (job: Job) => void;
  onOpenJob: (id: number) => void;
}) {
  const list = useAsync(() => db.schedules.list(), []);
  const configs = useAsync(() => db.copyConfigs.list(), []);
  const [editing, setEditing] = useState<{ value: Form; isNew: boolean } | null>(null);
  const [removing, setRemoving] = useState<Schedule | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const notice = useNotice();
  const rows = [...(list.data ?? [])].sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
  const canAdd = scopes.backup || scopes.copy;
  const configName = (id: string | null) => {
    const c = configs.data?.find((x) => x.id === id);
    return c ? c.name || c.id : (id ?? '');
  };

  const toggle = async (s: Schedule, enabled: boolean) => {
    setBusy(s.id);
    try {
      await db.schedules.put(s.id, { ...inputOf(s), enabled });
      notice.success(`${s.name || s.id} ${enabled ? 'enabled' : 'disabled'}`);
      await list.reload();
    } catch (e) {
      notice.error(dbErrorText(e));
    } finally {
      setBusy(null);
    }
  };
  const runNow = async (s: Schedule) => {
    setBusy(s.id);
    try {
      onJob(await db.schedules.run(s.id));
    } catch (e) {
      notice.error(dbErrorText(e));
    } finally {
      setBusy(null);
    }
  };
  const blank = (): Form => ({
    id: '',
    name: '',
    kind: scopes.backup ? 'backup' : 'copy',
    cron: CRON_PRESETS[0].cron,
    timezone: DEFAULT_TZ,
    serverId: '',
    database: '',
    configId: '',
    enabled: true,
  });

  return (
    <>
      <div className="dbx-toolbar">
        <span className="dbx-muted">
          Recurring backups and copies; times are in each schedule&apos;s time zone.
        </span>
        <span className="dbx-spacer" />
        {canAdd && (
          <Button size="sm" onClick={() => setEditing({ value: blank(), isNew: true })}>
            New schedule
          </Button>
        )}
      </div>
      <ErrorBox error={list.error} />
      <Loading loading={list.loading && !list.data} />
      {list.data && !rows.length && <EmptyState card title="No schedules yet" />}
      {!!rows.length && (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="schedules">
              <thead>
                <tr>
                  <th>Schedule</th>
                  <th>Kind</th>
                  <th>When</th>
                  <th>Next run</th>
                  <th>Last run</th>
                  <th>Enabled</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const mine = canRunKind(scopes, s.kind);
                  const text = cronToText(s.cron);
                  return (
                    <tr key={s.id} data-row={s.id}>
                      <td className="nyk-card-title">
                        <b>{s.name || s.id}</b>
                        <div className="nyk-ui-hint">
                          {s.kind === 'backup' && s.backup ? (
                            <Target
                              servers={servers}
                              server={s.backup.serverId}
                              database={s.backup.database}
                            />
                          ) : (
                            configName(s.configId)
                          )}
                        </div>
                      </td>
                      <td data-label="Kind">{s.kind}</td>
                      <td data-label="When">
                        <span title={s.cron}>{text ?? <code>{s.cron}</code>}</span>
                        <div className="nyk-ui-hint">{s.timezone}</div>
                      </td>
                      <td data-label="Next run">
                        {s.enabled && s.nextRunAt ? (
                          <>
                            {formatDateTime(s.nextRunAt)}
                            <div className="nyk-ui-hint">{formatRelative(s.nextRunAt)}</div>
                          </>
                        ) : (
                          <span className="dbx-muted">—</span>
                        )}
                      </td>
                      <td data-label="Last run">
                        {s.lastRunAt ? formatRelative(s.lastRunAt) : <span className="dbx-muted">never</span>}
                        {s.lastJobId != null && (
                          <div>
                            <button
                              type="button"
                              className="nyk-link"
                              onClick={() => onOpenJob(s.lastJobId!)}
                            >
                              job #{s.lastJobId}
                            </button>
                          </div>
                        )}
                      </td>
                      <td data-label="Enabled">
                        <Switch
                          checked={s.enabled}
                          disabled={!mine || busy === s.id}
                          onChange={(on) => void toggle(s, on)}
                          label={<span className="dbx-sr">Enabled {s.name || s.id}</span>}
                        />
                      </td>
                      <td className="nyk-row-actions">
                        {mine && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              loading={busy === s.id}
                              onClick={() => void runNow(s)}
                            >
                              Run now
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditing({ value: toForm(s), isNew: false })}
                            >
                              Edit
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setRemoving(s)}>
                              Delete
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      {editing && (
        <ScheduleForm
          initial={editing.value}
          isNew={editing.isNew}
          scopes={scopes}
          servers={servers}
          configs={configs.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={(s) => {
            setEditing(null);
            notice.success(`Saved ${s.name || s.id}`);
            void list.reload();
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          title={`Delete ${removing.name || removing.id}`}
          confirmLabel="Delete"
          danger
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            await db.schedules.remove(removing.id);
            setRemoving(null);
            notice.success(`Deleted ${removing.name || removing.id}`);
            void list.reload();
          }}
        >
          <p>Stops future runs. Past jobs and their backups are kept.</p>
        </ConfirmDialog>
      )}
      {notice.element}
    </>
  );
}
